import {useEffect, useRef, useState, type ReactNode, type Ref} from "react";
import {useTranslation} from "react-i18next";
import {TocTickRail} from "./TocTickRail";
import {buildTocTree, flattenTocEntries} from "./tocUtils";
import type {DibDocument} from "./types";
import classes from "./CompactToc.module.css";

const CLOSE_GRACE_MS = 120;

/**
 * The TOC of a narrow embedded view: a decorative tick rail, one tick per
 * TOC entry marked against the active entry, that opens the TOC list as a
 * flyout on hover or focus. A short grace lets the pointer cross from the
 * rail into the list; Escape closes it. The list (children) stays in the
 * DOM and in the tab order while closed: it is the accessible navigation,
 * and the rail is aria-hidden. The flyout is the scrolling TOC pane, so it
 * takes the scroll-sync hook's nav ref.
 */
export const CompactToc = ({
    document: doc,
    activeId,
    navRef,
    children,
}: {
    document: DibDocument;
    activeId: string | null;
    navRef: Ref<HTMLElement>;
    children: ReactNode;
}) => {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);
    const closeTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

    const show = () => {
        clearTimeout(closeTimer.current);
        setOpen(true);
    };
    const hide = () => {
        clearTimeout(closeTimer.current);
        setOpen(false);
    };
    const hideSoon = () => {
        clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(() => setOpen(false), CLOSE_GRACE_MS);
    };

    useEffect(() => () => clearTimeout(closeTimer.current), []);

    useEffect(() => {
        if (!open) return;
        const closeOnEscape = (e: KeyboardEvent) => {
            if (e.key === "Escape") setOpen(false);
        };
        document.addEventListener("keydown", closeOnEscape);
        return () => document.removeEventListener("keydown", closeOnEscape);
    }, [open]);

    // Rebuilt on each render (the active entry moves with scrolling): linear
    // in TOC size, so no memo.
    const entries = flattenTocEntries(buildTocTree(doc));
    // A topic without a TOC gets no control at all, rather than an empty
    // focusable button that opens an empty flyout.
    if (entries.length === 0) return null;

    return (
        <div
            className={classes.control}
            onFocus={show}
            onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) hide();
            }}
        >
            <div
                className={classes.railButton}
                role="button"
                tabIndex={0}
                aria-label={t("Title.LabelOfContents", {
                    defaultValue: "Table of contents",
                })}
                aria-expanded={open}
                onPointerEnter={show}
                onPointerLeave={hideSoon}
            >
                <TocTickRail
                    entries={entries}
                    currentId={activeId}
                    hidden={open}
                />
            </div>
            <nav
                ref={navRef}
                className={
                    open ? `${classes.flyout} ${classes.open}` : classes.flyout
                }
                onPointerEnter={show}
                onPointerLeave={hideSoon}
            >
                {children}
            </nav>
        </div>
    );
};
